// FirmwareModal.tsx - Dynamisches Laden der Firmware-Dateien mit 30s Timeout

import {
  IonModal,
  IonButtons,
  IonHeader,
  IonToolbar,
  IonContent,
  IonCard,
  IonCardContent,
  IonText,
  IonItem,
  IonLabel,
  IonIcon,
  IonProgressBar,
  IonChip,
  IonBadge,
} from '@ionic/react';
import UpdateOverlay from 'pages/Profile/components/UpdateOverlay';
import { useCallback, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useFSM } from 'services/DongleController/DongleController';
import Trans from 'util/Trans';
import DynamicButton from 'components/buttons/DynamicButton';
import {
  informationCircleOutline,
  checkmark,
  closeCircle,
  hourglass,
  alertCircle,
  bluetooth,
} from 'ionicons/icons';
import './FirmwareModal.css';
import HStack from 'components/HStack';
import VStack from 'components/VStack';
import { useAuthService } from 'services/Authentication/AuthService';
import { runInAction } from 'mobx';
import useFirmwareUpdate from '../../hooks/useFirmwareUpdate';

// --- NEU: Props für FirmwareModal ---
interface FirmwareModalProps {
  isOpen: boolean;
  onClose: () => void;
  firmwareUpdate: ReturnType<typeof useFirmwareUpdate>;
}

interface UpdateStatus {
  fileName: string;
  status:
    | 'pending'
    | 'initializing'
    | 'uploading'
    | 'validating'
    | 'installing'
    | 'completed'
    | 'failed';
  progress?: number;
  message?: string;
  startTime?: Date;
  endTime?: Date;
  errorDetails?: string;
}

interface FirmwareFile {
  name: string;
  label: string;
  description: string;
  type: string;
  category: string;
  size: string;
  recommended: boolean;
  warning?: string;
}

const FirmwareModal = observer(({ isOpen, onClose, firmwareUpdate }: FirmwareModalProps) => {
  const fsm = useFSM();
  const auth = useAuthService();

  // Nur noch lokale States für UI-Elemente, nicht für Firmware-Update-Status
  const [showUpdateOverlay, setShowUpdateOverlay] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [availableFirmware, setAvailableFirmware] = useState<FirmwareFile[]>(
    []
  );
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [operationLog, setOperationLog] = useState<string[]>([]);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Statt useFirmwareUpdate() direkt zu verwenden, Props nutzen:
  const { checkForUpdate, logs, clearLogs, version } = firmwareUpdate;

  // MobX-State aus FSMService
  const isUpdating = fsm.isUpdatingFirmware;
  const currentUpdate = fsm.firmwareUpdateStatus.get();
  const updateHistory = fsm.firmwareUpdateHistory;
  const uploadProgress = fsm.firmwareUpdateProgress.get();
  const uploadTotal = fsm.firmwareUpdateTotal.get();

  // isConnected muss vor den Button-Handlern stehen!
  const isConnected = fsm.bleState === 'CONNECTED';

  // ✅ FIXED: Add loadFirmwareFiles function
  const loadFirmwareFiles = useCallback(() => {
    setIsLoadingFiles(true);
    setLoadError(null);
    addLogEntry('🔄 Loading firmware files...');

    try {
      // Nur Beta und Production, kein Reset mehr!
      const firmwareList: FirmwareFile[] = [
        {
          name: 'v2.0.0-beta.bin',
          label: 'Beta Firmware v2.0.0',
          description: 'Latest beta version with experimental features',
          type: 'Beta',
          category: 'Testing',
          size: '2.1 MB',
          recommended: false,
          warning: 'Beta software - use with caution',
        },
        {
          name: 'production_firmware.bin',
          label: 'Production Firmware',
          description: 'Stable production firmware with bug fixes',
          type: 'Stable',
          category: 'Production',
          size: '1.8 MB',
          recommended: true,
        },
      ];

      setAvailableFirmware(firmwareList);

      // Auto-select recommended firmware
      const recommended = firmwareList.find((f) => f.recommended);
      if (recommended) {
        setSelectedFile(recommended.name);
      }

      addLogEntry(`✅ Loaded ${firmwareList.length} firmware files`);
      setIsLoadingFiles(false);
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'Failed to load firmware files';
      setLoadError(errorMsg);
      addLogEntry(`❌ Error loading firmware: ${errorMsg}`);
      setIsLoadingFiles(false);
    }
  }, []);

  // Load firmware files on mount
  useEffect(() => {
    if (isOpen) {
      loadFirmwareFiles();
    }
  }, [isOpen, loadFirmwareFiles]);

  // History beim ersten Mount laden
  useEffect(() => {
    if (isOpen) {
      fsm.loadFirmwareUpdateHistory();
    }
  }, [isOpen, fsm]);

  const addLogEntry = useCallback((message: string) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const logEntry = `[${timestamp}] ${message}`;
    setOperationLog((prev: string[]) => [...prev.slice(-49), logEntry]);
    console.log(`FIRMWARE UPDATE: ${logEntry}`);
  }, []);

  // History-Eintrag hinzufügen (global)
  const addToUpdateHistory = useCallback(
    (status: UpdateStatus) => {
      runInAction(() => {
        fsm.addFirmwareUpdateHistory(status);
      });
      addLogEntry(
        `📝 Added ${status.fileName} to update history (${status.status})`
      );
    },
    [addLogEntry, fsm]
  );

  const getUpdateDuration = (status: UpdateStatus): string => {
    if (!status.startTime) return '0s'; // statt 'N/A'
    const end = status.endTime || new Date();
    const duration = Math.round(
      (end.getTime() - status.startTime.getTime()) / 1000
    );

    if (duration < 60) return `${duration}s`;
    if (duration < 3600)
      return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  // Update den globalen MobX-State direkt
  const updateCurrentStatus = useCallback(
    (fileName: string, updates: Partial<UpdateStatus>) => {
      const prev = fsm.firmwareUpdateStatus.get();
      runInAction(() => {
        if (!prev || prev.fileName !== fileName) {
          fsm.firmwareUpdateStatus.set({
            fileName,
            status: 'pending',
            startTime: new Date(),
            ...updates,
          });
        } else {
          fsm.firmwareUpdateStatus.set({ ...prev, ...updates });
        }
      });
    },
    [fsm]
  );

  // ✅ FIXED: Three separate button handlers using the hook
  const handleBetaUpdate = useCallback(() => {
    if (!isConnected) return;
    setOperationLog([]); // Log im Modal leeren
    setPendingState('setup');
    addLogEntry('🚀 Starting Beta Update...');
    checkForUpdate("beta");
  }, [firmwareUpdate, addLogEntry, isConnected]);

  const handleProductionUpdate = useCallback(() => {
    if (!isConnected) return;
    setOperationLog([]); // Log im Modal leeren
    setPendingState('setup');
    addLogEntry('🚀 Starting Production Update...');
    checkForUpdate("production");
  }, [firmwareUpdate, addLogEntry, isConnected]);

  const handleCancelUpdate = useCallback(() => {
    addLogEntry('🛑 Cancelling firmware update...');
    // firmwareHook.cancelUpdate();
  }, [firmwareUpdate, addLogEntry]);

  // Hook-Logs in das Modal-Log integrieren
  useEffect(() => {
    if (logs && logs.length > 0) {
      const lastLog = logs[logs.length - 1];
      if (lastLog && !operationLog.some((log) => log.includes(lastLog))) {
        setOperationLog((prev) => [
          ...prev,
          `[${new Date().toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })}] ${lastLog}`,
        ]);
      }
    }
  }, [logs]);

  const clearUpdateHistory = useCallback(() => {
    fsm.clearFirmwareUpdateHistory();
    setOperationLog([]);
    addLogEntry('🗑️ Update history cleared');
  }, [addLogEntry, fsm]);

  const handleFirmwareUpdateEvent = (event: any) => {
    if (event.status === 'uploadProgressChanged' && event.data) {
      const { bytesSent, imageSize } = event.data;
      if (typeof bytesSent === 'number' && typeof imageSize === 'number') {
        fsm.setFirmwareUpdateProgress(bytesSent, imageSize);
      }
    }
  };

  // NEU: Tick-State, um die Daueranzeige kontinuierlich zu aktualisieren
  const [__durationTick, __setDurationTick] = useState(0);
  useEffect(() => {
    if (!currentUpdate) return;
    const isActive = !['completed', 'failed'].includes(currentUpdate.status);
    if (!isActive) return;
    const id = setInterval(() => __setDurationTick((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [currentUpdate?.startTime, currentUpdate?.status]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // ✅ Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Production':
        return 'success';
      case 'System':
        return 'danger';
      case 'Security':
        return 'warning';
      case 'Testing':
        return 'secondary';
      case 'Development':
        return 'medium';
      case 'Backup':
        return 'tertiary';
      default:
        return 'medium';
    }
  };

  // Kombinierte Logs für Live-Log-Stream
  const combinedLogs = Array.from(new Set([
    ...operationLog,
    ...(logs || [])
  ]));

  // Nur relevante Logs (ZDFU und echte Fehler/Status)
  const filteredLogs = combinedLogs.filter((log) => {
    // Zeige nur ZDFU-Logs oder Logs mit "Error"/"Fehler"/"failed"/"completed"
    return (
      log.includes('ZDFU') ||
      /error|fehler|failed|completed|aborted|abgebrochen|success|done/i.test(log)
    );
  });

  // Fortschritt aus ZDFU-Log extrahieren (letzter Wert)
  const lastZdfuLog = [...filteredLogs].reverse().find((log) => log.includes('ZDFU'));
  let zdfuPercent: number | null = null;
  let zdfuState: string | null = null;
  if (lastZdfuLog) {
    const match = lastZdfuLog.match(/"%":\s*([\d.]+)/);
    if (match) {
      zdfuPercent = parseFloat(match[1]);
    }
    // Extrahiere State aus {"state":{"state":"..."}}
    const stateMatch = lastZdfuLog.match(/"state"\s*:\s*\{\s*"state"\s*:\s*"([A-Z_]+)"\s*\}/i);
    if (stateMatch) {
      zdfuState = stateMatch[1];
    } else {
      // Extrahiere onUpgradeStarted/onUpgradeCompleted explizit
      const eventMatch = lastZdfuLog.match(/\{"(onUpgradeStarted|onUpgradeCompleted)":\{"state":"([A-Za-z_]+)"\}\}/);
      if (eventMatch) {
        if (eventMatch[1] === 'onUpgradeStarted') {
          zdfuState = 'Update gestartet';
        } else if (eventMatch[1] === 'onUpgradeCompleted') {
          zdfuState = 'Update abgeschlossen';
        } else {
          zdfuState = eventMatch[1];
        }
      }
    }
  }
  // Fallback: MobX-State Progress
  let fallbackPercent: number | null = null;
  if (zdfuPercent === null && uploadProgress !== null && uploadTotal !== null && uploadTotal > 0) {
    fallbackPercent = (uploadProgress / uploadTotal) * 100;
  }

  // Lokaler State für Setup-Phase
  const [pendingState, setPendingState] = useState<string | null>(null);

  // Sobald ein echter State/Prozentwert kommt, Setup-Status zurücksetzen
  useEffect(() => {
    if (zdfuState || zdfuPercent !== null) {
      if (pendingState) setPendingState(null);
    }
  }, [zdfuState, zdfuPercent, pendingState]);

  return (
    <>
      <IonModal
        isOpen={isOpen}
        onDidDismiss={onClose}
        className="edit-modal"
        backdropDismiss={false}
      >
        <IonHeader class="ion-no-border">
          <IonToolbar className="edit-modal-toptoolbar">
            <IonButtons slot="end">
              <DynamicButton
                text={<Trans i18nKey="CANCEL" />}
                iconName="CancelUpIcon"
                buttonType="icon"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                onClick={onClose}
              />
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent
          className="ion-padding"
          style={{ '--background': 'transparent', '--border-colour': 0 }}
        >
          <VStack style={{ justifyContent: 'flex-start' }}>
            {/* Connection Status Card */}
            <IonCard className="ion-card" style={{ minHeight: '70px' }}>
              <IonCardContent>
                <HStack style={{ alignItems: 'center', gap: '12px' }}>
                  <IonIcon
                    icon={bluetooth}
                    color={isConnected ? 'success' : 'warning'}
                    style={{ fontSize: '2rem', minWidth: '32px' }}
                  />
                  <VStack style={{ flex: '1', justifyContent: 'flex-start' }}>
                    <h3
                      style={{
                        margin: '0 0 4px 0',
                        color: 'var(--ion-color-light)',
                        fontWeight: 'bold',
                      }}
                    >
                      <Trans i18nKey="CONNECTION_STATUS">
                        Connection Status
                      </Trans>
                    </h3>
                    <p
                      style={{
                        color: isConnected
                          ? 'var(--ion-color-success)'
                          : 'var(--ion-color-warning)',
                        margin: '0',
                        fontSize: '14px',
                      }}
                    >
                      {isConnected ? (
                        <Trans i18nKey="DEVICE_READY">
                          FREAK ready for updates
                        </Trans>
                      ) : (
                        <Trans i18nKey="DEVICE_NOT_CONNECTED">
                          FREAK not connected
                        </Trans>
                      )}
                    </p>
                    {/* Firmware-Version entfernt */}
                  </VStack>
                </HStack>
              </IonCardContent>
            </IonCard>

            {/* Three Update Buttons */}
            <IonCard className="ion-card" style={{ minHeight: '170px' }}>
              <IonCardContent>
                <VStack style={{ justifyContent: 'flex-start' }}>
                  <IonText>
                    <h3
                      style={{
                        margin: '0',
                        color: 'var(--ion-color-light)',
                        fontWeight: 'bold',
                      }}
                    >
                      <Trans i18nKey="UPDATE_ACTIONS">Firmware Update</Trans>
                    </h3>
                  </IonText>

                  <IonText
                    color="medium"
                    style={{ fontSize: 14, marginTop: 4 }}
                  >
                    <Trans i18nKey="UPDATE_DESCRIPTION">
                      If not connected, please connect your FREAK in the device
                      section. Then start the desired update. BLE will disconnect and shortly after the update will start.
                      If you encounter repeated errors please contact: info@spinnax.com.
                    </Trans>
                  </IonText>

                  <VStack style={{ gap: '12px', justifyContent: 'flex-start' }}>
                    <DynamicButton
                      text={<Trans i18nKey="BETA_UPDATE">Beta Update</Trans>}
                      buttonType="text"
                      color="secondary"
                      size="large"
                      fill="solid"
                      expand="block"
                      disabled={isUpdating || !isConnected}
                      showSpinner={isUpdating}
                      onClick={handleBetaUpdate}
                      className="spnx-button"
                    />

                    <DynamicButton
                      text={
                        <Trans i18nKey="PRODUCTION_UPDATE">
                          Production Update
                        </Trans>
                      }
                      buttonType="text"
                      color="primary"
                      size="large"
                      fill="solid"
                      expand="block"
                      disabled={isUpdating || !isConnected}
                      showSpinner={isUpdating}
                      onClick={handleProductionUpdate}
                      className="spnx-button"
                    />

                    {/* Cancel Button - nur anzeigen wenn Update läuft */}
                    {isUpdating && (
                      <DynamicButton
                        text={
                          <Trans i18nKey="CANCEL_UPDATE">
                            Update abbrechen
                          </Trans>
                        }
                        buttonType="text"
                        color="danger"
                        size="large"
                        fill="solid"
                        expand="block"
                        onClick={handleCancelUpdate}
                        className="spnx-button"
                        style={{
                          backgroundColor: '#e74c3c',
                          color: '#ffffff',
                          borderColor: '#e74c3c',
                          borderRadius: '12px',
                        }}
                      />
                    )}
                  </VStack>
                </VStack>
              </IonCardContent>
            </IonCard>

            {/* Current Update Status Card: Fortschritt und Status aus Logs */}
            <IonCard
              className="ion-card"
              style={{
                minHeight: '160px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                // Hintergrund wie andere Cards
                background: 'var(--ion-card-background)',
                border: 'none',
              }}
            >
              <IonCardContent style={{ padding: '20px 16px' }}>
                {/* Fortschritt und Status */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: lastZdfuLog ? '#fff' : 'var(--ion-color-medium)',
                      fontSize: '16px',
                    }}
                  >
                    {/* Immer gleiche Überschrift */}
                    <Trans i18nKey="UPDATE_ACTIONS">Firmware Update</Trans>
                  </span>
                  <IonChip
                    color={(() => {
                      if (
                        (zdfuState === 'completed' || zdfuState === 'Update abgeschlossen' || zdfuState === 'onUpgradeCompleted') &&
                        zdfuPercent !== null && zdfuPercent > 0
                      ) {
                        return 'success'; // grün
                      }
                      if (zdfuState && /failed|aborted|abgebrochen|error|fehler/i.test(zdfuState)) {
                        return 'danger'; // rot
                      }
                      return 'secondary'; // orange/standard
                    })()}
                    style={{
                      background: (() => {
                        if (
                          (zdfuState === 'completed' || zdfuState === 'Update abgeschlossen' || zdfuState === 'onUpgradeCompleted') &&
                          zdfuPercent !== null && zdfuPercent > 0
                        ) {
                          return 'var(--ion-color-success)';
                        }
                        if (zdfuState && /failed|aborted|abgebrochen|error|fehler/i.test(zdfuState)) {
                          return 'var(--ion-color-danger)';
                        }
                        return 'var(--ion-color-secondary)';
                      })(),
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      padding: '8px 16px',
                      boxShadow: lastZdfuLog ? '0 0 15px rgba(243, 156, 18, 0.8)' : 'none',
                    }}
                  >
                    <IonIcon icon={hourglass} style={{ color: '#fff' }} />
                    <IonLabel style={{ color: '#fff', fontWeight: 'bold' }}>
                      {(() => {
                        if (pendingState === 'setup') return 'Setup';
                        if (zdfuState === 'onUpgradeCompleted') return 'SUCCESS';
                        if (zdfuState) return zdfuState;
                        if (zdfuPercent !== null) return 'RUNNING';
                        return lastZdfuLog ? 'RUNNING' : 'Idle';
                      })()}
                    </IonLabel>
                  </IonChip>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginTop: 20, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* Fortschritt-Logik: Upload max. 95%, erst bei completed 100% */}
                        {(() => {
                          let progressValue = 0;
                          if (zdfuPercent !== null) {
                            if (zdfuState === 'uploading') {
                              progressValue = Math.min(zdfuPercent, 95) / 100;
                            } else if (zdfuState === 'completed' || zdfuState === 'Update abgeschlossen') {
                              progressValue = 1;
                            } else if (zdfuPercent >= 95) {
                              progressValue = 0.95;
                            } else {
                              progressValue = zdfuPercent / 100;
                            }
                          }
                          return (
                            <IonProgressBar
                              value={progressValue}
                              color={lastZdfuLog ? 'warning' : 'medium'}
                              style={{
                                borderRadius: 12,
                                height: 24,
                                boxShadow: lastZdfuLog ? '0 0 20px rgba(255, 193, 7, 0.6)' : 'none',
                                background: lastZdfuLog ? 'rgba(0, 0, 0, 0.3)' : 'var(--ion-color-step-150)',
                                flex: 1,
                                border: lastZdfuLog ? '2px solid rgba(255, 193, 7, 0.3)' : '2px solid var(--ion-color-step-150)',
                              }}
                            />
                          );
                        })()}
                        <IonBadge
                          color={lastZdfuLog ? 'light' : 'medium'}
                          style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: '8px 12px',
                          }}
                        >
                          {(() => {
                            if (pendingState === 'setup') return 'Setup';
                            // Immer 100% bei abgeschlossenem Update oder Bereits aktuell
                            if (
                              (zdfuState === 'completed' || zdfuState === 'Update abgeschlossen' || zdfuState === 'onUpgradeCompleted')
                            ) {
                              return '100%';
                            }
                            if (zdfuPercent !== null) {
                              return `${Math.min(zdfuPercent, 100).toFixed(1)}%`;
                            }
                            // Kein Prozentwert, kein Abschluss: 0%
                            return '0%';
                          })()}
                        </IonBadge>
                      </div>
                    </div>
                    {/* Ziel-Firmware-Version jetzt hier anzeigen */}
                    {(() => {
                      const receivedLog = combinedLogs.find((log) => /Firmware received: ([^ ]+)/.test(log));
                      let receivedVersion = null;
                      if (receivedLog) {
                        const m = receivedLog.match(/Firmware received: ([^ ]+)/);
                        if (m) receivedVersion = m[1];
                      }
                      let updateFile = currentUpdate?.fileName;
                      // Ersetze _ durch . für die Anzeige
                      const displayVersion = (receivedVersion || updateFile)?.replace(/_/g, '.');
                      if (displayVersion) {
                        return (
                          <p style={{ color: '#fff', fontWeight: 500, margin: '8px 0 0 0' }}>
                            <Trans i18nKey="TARGET_FIRMWARE_VERSION">Installierte Version</Trans>: {displayVersion}
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          </VStack>
        </IonContent>
      </IonModal>

      {/* Update Overlay */}
      {showUpdateOverlay && currentUpdate && (
        <UpdateOverlay
          updateStatus={currentUpdate as any}
          dismiss={() => {
            setShowUpdateOverlay(false);
            runInAction(() => {
              fsm.firmwareUpdateStatus.set(null);
            });
            onClose();
          }}
        />
      )}

      {/* Firmware-Update-Logs anzeigen (bisherige Log-Card bleibt als History erhalten) */}
      <IonCard className="ion-card" style={{ marginTop: 16 }}>
        <IonCardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontWeight: 'bold' }}>Logs</h3>
            <DynamicButton
              text={<Trans i18nKey="CLEAR_LOGS">Logs löschen</Trans>}
              buttonType="text"
              color="medium"
              size="small"
              fill="outline"
              onClick={clearLogs}
              className="spnx-button"
            />
          </div>
          <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 13, marginTop: 8 }}>
            {filteredLogs.length > 0
              ? filteredLogs.map((log, idx) => <div key={idx}>{log}</div>)
              : <span style={{ color: '#888' }}>Keine Logs vorhanden</span>}
          </pre>
        </IonCardContent>
      </IonCard>
    </>
  );
});

export default FirmwareModal;
